import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Tag, Typography, Space, Alert, Empty,
  Popconfirm, Dropdown, Modal, DatePicker, TimePicker, Select, message, Input, Tooltip, Radio, InputNumber, Switch, theme as antdTheme,
} from 'antd';
import {
  PlusOutlined, SendOutlined, DeleteOutlined, MoreOutlined,
  ClockCircleOutlined, MailOutlined, SearchOutlined, EditOutlined,
  CopyOutlined, ExperimentOutlined, BarChartOutlined,
  EyeOutlined, TeamOutlined, RiseOutlined, StopOutlined,
  FileTextOutlined, CheckCircleOutlined, SyncOutlined, ScheduleOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const TIMEZONES = [
  { label: 'UTC (UTC+0)',                      value: 'UTC' },
  { label: 'London (UTC+0/+1)',                value: 'Europe/London' },
  { label: 'Paris / Berlin (UTC+1/+2)',        value: 'Europe/Paris' },
  { label: 'Helsinki / Kyiv (UTC+2/+3)',       value: 'Europe/Helsinki' },
  { label: 'Moscow (UTC+3)',                   value: 'Europe/Moscow' },
  { label: 'Dubai (UTC+4)',                    value: 'Asia/Dubai' },
  { label: 'Karachi (UTC+5)',                  value: 'Asia/Karachi' },
  { label: 'India (UTC+5:30)',                 value: 'Asia/Kolkata' },
  { label: 'Dhaka (UTC+6)',                    value: 'Asia/Dhaka' },
  { label: 'Bangkok / Jakarta (UTC+7)',        value: 'Asia/Bangkok' },
  { label: 'Singapore / Beijing (UTC+8)',      value: 'Asia/Singapore' },
  { label: 'Tokyo / Seoul (UTC+9)',            value: 'Asia/Tokyo' },
  { label: 'Sydney (UTC+10/+11)',              value: 'Australia/Sydney' },
  { label: 'Auckland (UTC+12/+13)',            value: 'Pacific/Auckland' },
  { label: 'Honolulu (UTC-10)',                value: 'Pacific/Honolulu' },
  { label: 'Anchorage (UTC-9/-8)',             value: 'America/Anchorage' },
  { label: 'Los Angeles / Seattle (UTC-8/-7)', value: 'America/Los_Angeles' },
  { label: 'Denver / Phoenix (UTC-7/-6)',      value: 'America/Denver' },
  { label: 'Chicago / Dallas (UTC-6/-5)',      value: 'America/Chicago' },
  { label: 'New York / Miami (UTC-5/-4)',      value: 'America/New_York' },
  { label: 'Toronto (UTC-5/-4)',               value: 'America/Toronto' },
  { label: 'São Paulo (UTC-3)',                value: 'America/Sao_Paulo' },
];

// Weekday values match the backend's convention: Date.getUTCDay() —
// 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat. Displayed Monday-first.
const WEEKDAYS = [
  { label: 'M',   full: 'Monday',    value: 1 },
  { label: 'T',   full: 'Tuesday',   value: 2 },
  { label: 'W',   full: 'Wednesday', value: 3 },
  { label: 'T',   full: 'Thursday',  value: 4 },
  { label: 'F',   full: 'Friday',    value: 5 },
  { label: 'S',   full: 'Saturday',  value: 6 },
  { label: 'Sun', full: 'Sunday',    value: 0 },
];
const dayLabel = (v) => (WEEKDAYS.find(d => d.value === v)?.full.slice(0, 3)) || '?';

// Convert wall-clock datetime (as seen in `tz`) to a UTC Date object.
const toUTCForTimezone = (dayjsVal, tz) => {
  // Build a neutral UTC timestamp from the wall-clock parts, avoiding browser tz interference
  const wallClock = new Date(Date.UTC(
    dayjsVal.year(), dayjsVal.month(), dayjsVal.date(), dayjsVal.hour(), dayjsVal.minute(), 0,
  ));
  // Measure the UTC offset of `tz` at this approximate moment using Intl
  const parts = (zone) => new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(wallClock).reduce((a, p) => { if (p.type !== 'literal') a[p.type] = +p.value; return a; }, {});
  const toMs = (o) => Date.UTC(o.year, o.month - 1, o.day, o.hour % 24, o.minute, o.second);
  const offsetMs = toMs(parts(tz)) - toMs(parts('UTC')); // positive = ahead of UTC
  return new Date(wallClock.getTime() - offsetMs);
};

// Convert a chosen time-of-day (dayjs, hour/minute only) in `tz` to a UTC
// "HH:mm" string, anchored to today's date in `tz` for the conversion.
const toUTCTimeString = (dayjsTime, tz) => {
  const anchored = dayjs().hour(dayjsTime.hour()).minute(dayjsTime.minute()).second(0);
  const utc = toUTCForTimezone(anchored, tz);
  return `${String(utc.getUTCHours()).padStart(2, '0')}:${String(utc.getUTCMinutes()).padStart(2, '0')}`;
};

const STATUS_CONFIG = {
  draft:     { color: 'default',    label: 'Draft' },
  scheduled: { color: 'processing', label: 'Scheduled' },
  sending:   { color: 'warning',    label: 'Sending' },
  dripping:  { color: 'cyan',       label: 'Dripping' },
  completed: { color: 'success',    label: 'Completed' },
};

const Campaigns = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [campaignStats, setCampaignStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleIsReschedule, setScheduleIsReschedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTz, setScheduleTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [scheduling, setScheduling] = useState(false);
  const [testModal, setTestModal] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [sendModal, setSendModal] = useState(null);   // campaign id
  const [sendMode, setSendMode] = useState('all');     // 'all' | 'daily'
  const [sendDaily, setSendDaily] = useState(200);
  const [sendDayMode, setSendDayMode] = useState('every'); // 'every' | 'specific'
  const [sendDaysSelected, setSendDaysSelected] = useState([1, 2, 3, 4, 5]); // default weekdays
  const [sendTiming, setSendTiming] = useState('now'); // 'now' | 'schedule'
  const [sendStartDate, setSendStartDate] = useState(null);
  const [sendStartTz, setSendStartTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [sendFixedTime, setSendFixedTime] = useState(false);   // anchor every batch to a clock time
  const [sendDailyClock, setSendDailyClock] = useState(null);  // dayjs — used in "every day" mode
  const [sendDayTimes, setSendDayTimes] = useState({});        // { [dayValue]: dayjs } — used in "specific days" mode
  const [sendDailyTz, setSendDailyTz] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [sending2, setSending2] = useState(false);
  const navigate = useNavigate();

  const fetchCampaigns = async (p = page) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: p, limit: PAGE_SIZE, archived: showArchived };
      if (search.trim()) params.search = search.trim();
      const res = await axios.get('/api/campaigns', { params });
      setCampaigns(res.data.data);
      setTotal(res.data.total);
      // Fetch stats for all sent/sending/completed campaigns in parallel
      const sent = res.data.data.filter(c => c.status !== 'draft' && c.status !== 'scheduled');
      const results = await Promise.allSettled(
        sent.map(c => axios.get(`/api/campaigns/${c.id}/stats`))
      );
      const statsMap = {};
      sent.forEach((c, i) => {
        if (results[i].status === 'fulfilled') statsMap[c.id] = results[i].value.data;
      });
      setCampaignStats(statsMap);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(1); setPage(1); }, [showArchived]);
  useEffect(() => { fetchCampaigns(page); }, [page]);
  // Debounced server-side search across the whole campaign list
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchCampaigns(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Toggle a weekday in/out of sendDaysSelected, keeping sendDayTimes in
  // sync — a newly-checked day gets a sensible default time (copied from
  // the most recently added day, or 9:00 AM), an unchecked day drops its entry.
  const toggleSendDay = (dayValue) => {
    setSendDaysSelected(prev => {
      const isActive = prev.includes(dayValue);
      if (isActive) {
        setSendDayTimes(pt => { const c = { ...pt }; delete c[dayValue]; return c; });
        return prev.filter(v => v !== dayValue);
      }
      setSendDayTimes(pt => {
        if (pt[dayValue]) return pt;
        const lastDay = prev[prev.length - 1];
        return { ...pt, [dayValue]: pt[lastDay] || dayjs().hour(9).minute(0) };
      });
      return [...prev, dayValue];
    });
  };

  const handleConfirmSend = async () => {
    const body = {};
    if (sendMode === 'daily' && sendDaily > 0) {
      body.dailyLimit = sendDaily;
      if (sendDayMode === 'specific') {
        if (!sendDaysSelected.length) { message.warning('Select at least one day'); return; }
        body.sendDays = sendDaysSelected;
      }
      if (sendFixedTime) {
        if (sendDayMode === 'specific') {
          const times = {};
          for (const d of sendDaysSelected) {
            const t = sendDayTimes[d];
            if (!t) { message.warning(`Pick a time for ${dayLabel(d)}`); return; }
            times[d] = toUTCTimeString(dayjs(t), sendDailyTz);
          }
          body.dayTimesUTC = times;
          body.dailyTimezone = sendDailyTz;
        } else {
          if (!sendDailyClock) { message.warning('Pick a daily send time'); return; }
          body.dailyTimeUTC = toUTCTimeString(dayjs(sendDailyClock), sendDailyTz);
          body.dailyTimezone = sendDailyTz;
        }
      }
    }
    if (sendTiming === 'schedule') {
      if (!sendStartDate) { message.warning('Pick a start date and time'); return; }
      const startAt = toUTCForTimezone(dayjs(sendStartDate), sendStartTz);
      if (startAt <= new Date()) { message.error('Start time must be in the future'); return; }
      body.scheduleAt = startAt.toISOString();
    } else {
      body.sendNow = true;
    }
    setSending2(true);
    try {
      const res = await axios.post(`/api/campaigns/${sendModal}/send`, body);
      message.success(res.data.message || 'Campaign queued');
      setSendModal(null);
      fetchCampaigns();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to send campaign');
    } finally {
      setSending2(false);
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate) { message.warning('Please select a date and time'); return; }
    const scheduleAt = toUTCForTimezone(dayjs(scheduleDate), scheduleTz);
    if (scheduleAt <= new Date()) { message.error('Scheduled time must be in the future'); return; }
    setScheduling(true);
    try {
      await axios.post(`/api/campaigns/${scheduleModal}/send`, { scheduleAt: scheduleAt.toISOString() });
      message.success(scheduleIsReschedule ? 'Campaign rescheduled' : 'Campaign scheduled');
      setScheduleModal(null);
      setScheduleDate(null);
      fetchCampaigns();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to schedule campaign');
    } finally {
      setScheduling(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/campaigns/${id}`);
      message.success('Campaign archived');
      fetchCampaigns();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to archive campaign');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`/api/campaigns/${id}/duplicate`, {});
      message.success('Campaign duplicated');
      fetchCampaigns();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to duplicate campaign');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    setTestSending(true);
    try {
      await axios.post(`/api/campaigns/${testModal}/test`, { email: testEmail });
      message.success(`Test email sent to ${testEmail}`);
      setTestModal(null);
      setTestEmail('');
    } catch (err) {
      message.error(err.response?.data?.message || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  // Search is applied server-side (spans all pages); the status tabs filter the
  // returned rows.
  const filtered = campaigns.filter(c => statusFilter === 'all' || c.status === statusFilter);

  const statCounts = {
    all:       campaigns.length,
    draft:     campaigns.filter(c => c.status === 'draft').length,
    scheduled: campaigns.filter(c => c.status === 'scheduled').length,
    sending:   campaigns.filter(c => c.status === 'sending').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
  };

  const aggregated = Object.values(campaignStats).reduce((acc, s) => {
    acc.totalSent    += s.total    || 0;
    acc.totalOpened  += (s.opened + s.clicked) || 0;
    acc.totalClicked += s.clicked  || 0;
    acc.totalBounced += s.bounced  || 0;
    return acc;
  }, { totalSent: 0, totalOpened: 0, totalClicked: 0, totalBounced: 0 });

  const columns = [
    {
      title: 'Campaign', dataIndex: 'name',
      render: (name, record) => (
        <Space size={10}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <MailOutlined style={{ color: '#6366f1', fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 600, color: themeToken.colorText, fontSize: 13 }}>{name}</div>
            <div style={{ color: themeToken.colorTextSecondary, fontSize: 12 }}>{record.subject}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 150,
      render: (s, record) => {
        const isDrip = s === 'sending' && record.daily_limit > 0;
        const cfg = isDrip ? STATUS_CONFIG.dripping : (STATUS_CONFIG[s] || STATUS_CONFIG.draft);
        const tag = <Tag color={cfg.color} style={{ fontWeight: 500 }}>{cfg.label}</Tag>;

        if (isDrip) {
          const target    = record.total_count || 0;
          const processed = campaignStats[record.id]?.total ?? 0;
          const remaining = Math.max(0, target - processed);
          return (
            <div>
              {tag}
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 11, color: themeToken.colorTextSecondary, display: 'block', whiteSpace: 'nowrap' }}>
                  {processed.toLocaleString()} sent · {remaining.toLocaleString()} left
                </Text>
                <Text style={{ fontSize: 11, color: themeToken.colorTextTertiary, display: 'block', whiteSpace: 'nowrap' }}>
                  {record.daily_limit.toLocaleString()}/day
                  {record.next_batch_at && ` · next ${new Date(record.next_batch_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`}
                </Text>
                {Array.isArray(record.send_days) && record.send_days.length > 0 && (
                  <Text style={{ fontSize: 11, color: themeToken.colorTextTertiary, display: 'block', whiteSpace: 'nowrap' }}>
                    {[...record.send_days].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b)).map(dayLabel).join(', ')} only
                  </Text>
                )}
              </div>
            </div>
          );
        }
        // Once a drip finishes, daily_limit/current_batch_number stay on the
        // row — surface that history so a completed drip is identifiable at
        // a glance, not just while it's actively sending.
        if (s === 'completed' && record.daily_limit > 0) {
          const batches = record.current_batch_number || 0;
          return (
            <div>
              {tag}
              <div style={{ marginTop: 4 }}>
                <Tooltip title={`Sent as a drip campaign — up to ${record.daily_limit.toLocaleString()}/day${batches ? ` over ${batches} batch${batches !== 1 ? 'es' : ''}` : ''}`}>
                  <Tag color="cyan" style={{ fontSize: 11, margin: 0, fontWeight: 500 }} icon={<HistoryOutlined />}>
                    Dripped{batches > 1 ? ` · ${batches} batches` : ''}
                  </Tag>
                </Tooltip>
              </div>
            </div>
          );
        }
        if (s === 'scheduled' && record.scheduled_at) {
          const d = new Date(record.scheduled_at);
          const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          const tzName   = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const tzAbbr   = d.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
          return (
            <div>
              {tag}
              <div style={{ marginTop: 4 }}>
                <Text style={{ fontSize: 11, color: themeToken.colorTextSecondary, display: 'block', whiteSpace: 'nowrap' }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />{datePart} · {timePart}
                </Text>
                <Text style={{ fontSize: 11, color: themeToken.colorTextTertiary, display: 'block' }}>
                  {tzName} ({tzAbbr})
                </Text>
              </div>
            </div>
          );
        }
        return tag;
      },
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 130, responsive: ['lg'],
      render: d => <Text style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>,
    },
    {
      title: 'Performance', key: 'stats', width: 260,
      render: (_, record) => {
        const s = campaignStats[record.id];
        if (record.status === 'draft' || record.status === 'scheduled' || !s) {
          return <Text style={{ color: themeToken.colorTextTertiary, fontSize: 12 }}>—</Text>;
        }
        const pills = [
          { icon: <TeamOutlined />,  value: s.total,          label: 'sent',   color: '#6366f1', bg: '#ede9fe' },
          { icon: <EyeOutlined />,   value: `${s.openRate}%`,  label: 'opened', color: '#9DC533', bg: '#EEF6D0' },
          { icon: <RiseOutlined />,  value: `${s.clickRate}%`, label: 'clicks', color: '#3b82f6', bg: '#dbeafe' },
          { icon: <StopOutlined />,  value: s.bounced,         label: 'bounced',color: '#ef4444', bg: '#fef2f2' },
        ];
        return (
          <Space size={6} wrap>
            {pills.map(p => (
              <div key={p.label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', borderRadius: 20,
                background: p.bg, border: `1px solid ${p.color}22`,
              }}>
                {React.cloneElement(p.icon, { style: { fontSize: 10, color: p.color } })}
                <Text style={{ fontSize: 11, fontWeight: 600, color: p.color }}>{p.value}</Text>
                <Text style={{ fontSize: 10, color: p.color + 'aa' }}>{p.label}</Text>
              </div>
            ))}
          </Space>
        );
      },
    },
    {
      title: '', key: 'actions', align: 'right', width: 80,
      render: (_, record) => {
        const isDraft = record.status === 'draft';
        const isScheduled = record.status === 'scheduled';
        const menuItems = [
          ...(isDraft ? [
            { key: 'edit', label: <Space><EditOutlined />Edit</Space>, onClick: () => navigate(`/campaigns/${record.id}/edit`) },
            { key: 'send', label: <Space><SendOutlined />Send…</Space>, onClick: () => { setSendMode('all'); setSendDaily(200); setSendDayMode('every'); setSendDaysSelected([1, 2, 3, 4, 5]); setSendFixedTime(false); setSendDailyClock(null); setSendDayTimes({}); setSendTiming('now'); setSendStartDate(null); setSendModal(record.id); } },
            { key: 'schedule', label: <Space><ClockCircleOutlined />Schedule</Space>, onClick: () => { setScheduleIsReschedule(false); setScheduleModal(record.id); } },
          ] : []),
          ...(isScheduled ? [
            { key: 'reschedule', label: <Space><ClockCircleOutlined />Reschedule</Space>, onClick: () => { setScheduleIsReschedule(true); setScheduleModal(record.id); } },
            {
              key: 'send-scheduled',
              label: <Space><SendOutlined />Send Now</Space>,
              onClick: () => Modal.confirm({
                title: 'Send campaign now?',
                content: (
                  <div>
                    <p style={{ marginBottom: 8 }}>
                      This will cancel the scheduled send and send the campaign <strong>immediately</strong> to all subscribers.
                    </p>
                    <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>
                      This action cannot be undone once sending begins.
                    </p>
                  </div>
                ),
                okText: 'Yes, Send Now',
                cancelText: 'Keep Schedule',
                okButtonProps: { style: { borderRadius: 8 } },
                cancelButtonProps: { style: { borderRadius: 8 } },
                onOk: () => handleSendNow(record.id),
              }),
            },
          ] : []),
          { key: 'test', label: <Space><ExperimentOutlined />Preview &amp; Send Test</Space>, onClick: () => { setTestModal(record.id); setTestEmail(''); } },
          { key: 'duplicate', label: <Space><CopyOutlined />Duplicate</Space>, onClick: () => handleDuplicate(record.id) },
          { type: 'divider' },
          {
            key: 'delete', danger: true,
            label: (
              <Popconfirm
                title="Archive this campaign?"
                description="It will be moved to the archive and hidden from the main list."
                onConfirm={() => handleDelete(record.id)}
                okText="Archive"
                okButtonProps={{ danger: true }}
                onClick={e => e.stopPropagation()}
              >
                <Space><DeleteOutlined />Archive</Space>
              </Popconfirm>
            ),
          },
        ];
        return (
          <div onClick={e => e.stopPropagation()}>
            <Space size={4}>
              {!isDraft && (
                <Button
                  icon={<BarChartOutlined />}
                  type="text"
                  size="small"
                  style={{ color: themeToken.colorPrimary }}
                  title="View Report"
                  onClick={() => navigate(`/campaigns/${record.id}/report`)}
                />
              )}
              <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                <Button icon={<MoreOutlined />} type="text" size="small" />
              </Dropdown>
            </Space>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>{showArchived ? 'Archived Campaigns' : 'Campaigns'}</Title>
          <Text style={{ color: themeToken.colorTextSecondary }}>{total} campaign{total !== 1 ? 's' : ''} {showArchived ? 'archived' : 'total'}</Text>
        </div>
        <Space>
          <Button
            icon={<DeleteOutlined />}
            onClick={() => { setShowArchived(v => !v); setStatusFilter('all'); }}
            style={{ borderRadius: 10 }}
            type={showArchived ? 'primary' : 'default'}
          >
            {showArchived ? 'Back to Active' : 'Archived'}
          </Button>
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => navigate('/campaigns/create')} style={{ borderRadius: 10, fontWeight: 600 }}>
              New Campaign
            </Button>
          )}
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />}

      {/* ── Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Campaigns Sent',  value: statCounts.completed,    color: '#6366f1', bg: '#ede9fe', icon: <SendOutlined />,         hint: 'Completed campaigns' },
          { label: 'Drafts',          value: statCounts.draft,         color: '#94a3b8', bg: '#f1f5f9', icon: <FileTextOutlined />,      hint: 'Campaigns not yet sent' },
          { label: 'Emails Delivered',value: aggregated.totalSent,     color: '#9DC533', bg: '#EEF6D0', icon: <CheckCircleOutlined />,   hint: 'Total individual emails delivered' },
          { label: 'Emails Opened',   value: aggregated.totalOpened,   color: '#3b82f6', bg: '#dbeafe', icon: <EyeOutlined />,           hint: 'Unique opens across all campaigns' },
          { label: 'Emails Clicked',  value: aggregated.totalClicked,  color: '#8b5cf6', bg: '#ede9fe', icon: <RiseOutlined />,          hint: 'Unique clicks across all campaigns' },
          { label: 'Bounced',         value: aggregated.totalBounced,  color: '#ef4444', bg: '#fef2f2', icon: <StopOutlined />,          hint: 'Total bounced emails' },
        ].map(({ label, value, color, bg, icon, hint }) => (
          <Card key={label} style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
            styles={{ body: { padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 } }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {React.cloneElement(icon, { style: { fontSize: 17, color } })}
            </div>
            <div>
              <Tooltip title={hint}>
                <div style={{ fontSize: 11, fontWeight: 500, color: themeToken.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'default' }}>{label}</div>
              </Tooltip>
              <div style={{ fontSize: 22, fontWeight: 800, color: themeToken.colorText, lineHeight: 1.2 }}>{value}</div>
            </div>
          </Card>
        ))}
      </div>

      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search campaigns…"
            prefix={<SearchOutlined style={{ color: themeToken.colorTextTertiary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', maxWidth: 260, borderRadius: 10 }}
            allowClear
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[
              { key: 'all',       label: `All (${statCounts.all})` },
              { key: 'draft',     label: `Draft (${statCounts.draft})` },
              { key: 'scheduled', label: `Scheduled (${statCounts.scheduled})` },
              { key: 'sending',   label: `Sending (${statCounts.sending})` },
              { key: 'completed', label: `Completed (${statCounts.completed})` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  border: `1px solid ${statusFilter === key ? themeToken.colorPrimary : themeToken.colorBorderSecondary}`,
                  background: statusFilter === key ? themeToken.colorPrimary : 'transparent',
                  color: statusFilter === key ? '#fff' : themeToken.colorTextSecondary,
                  transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          onRow={record => ({
            onClick: () => record.status === 'draft'
              ? navigate(`/campaigns/${record.id}/edit`)
              : navigate(`/campaigns/${record.id}/report`),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No campaigns yet. Create your first campaign!" /> }}
          pagination={{
            current: page, pageSize: PAGE_SIZE, total,
            onChange: (p) => setPage(p),
            showTotal: t => `${t} campaign${t !== 1 ? 's' : ''}`,
            showSizeChanger: false,
          }}
          scroll={{ x: 560 }}
        />
      </Card>

      {/* Send Options Modal */}
      <Modal
        title={<Space><SendOutlined style={{ color: '#9DC533' }} /><span>Send Campaign</span></Space>}
        open={!!sendModal}
        onCancel={() => setSendModal(null)}
        onOk={handleConfirmSend}
        okText={sendTiming === 'schedule' ? (sendMode === 'daily' ? 'Schedule Drip' : 'Schedule') : (sendMode === 'daily' ? 'Start Drip Send' : 'Send Now')}
        okButtonProps={{ loading: sending2, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={480}
        destroyOnHidden
      >
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>How to send</Text>
            <Radio.Group value={sendMode} onChange={e => setSendMode(e.target.value)} style={{ width: '100%' }}>
              <Radio.Button value="all" style={{ width: '50%', textAlign: 'center' }}>All at once</Radio.Button>
              <Radio.Button value="daily" style={{ width: '50%', textAlign: 'center' }}>Daily batches</Radio.Button>
            </Radio.Group>
          </div>

          {sendMode === 'daily' && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Emails per day</Text>
              <InputNumber
                min={1} max={100000} step={50}
                value={sendDaily}
                onChange={v => setSendDaily(v || 0)}
                style={{ width: '100%', borderRadius: 8 }}
                size="large"
              />
            </div>
          )}

          {sendMode === 'daily' && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Send on</Text>
              <Radio.Group value={sendDayMode} onChange={e => setSendDayMode(e.target.value)} style={{ width: '100%', marginBottom: sendDayMode === 'specific' ? 10 : 0 }}>
                <Radio.Button value="every" style={{ width: '50%', textAlign: 'center' }}>All days</Radio.Button>
                <Radio.Button value="specific" style={{ width: '50%', textAlign: 'center' }}>Specific days</Radio.Button>
              </Radio.Group>
              {sendDayMode === 'specific' && (
                <Space size={6} wrap>
                  {WEEKDAYS.map(d => {
                    const active = sendDaysSelected.includes(d.value);
                    return (
                      <Button
                        key={d.value}
                        size="small"
                        title={d.full}
                        onClick={() => toggleSendDay(d.value)}
                        style={{
                          borderRadius: 8, minWidth: 38, fontWeight: 600,
                          background: active ? '#9DC533' : undefined,
                          borderColor: active ? '#9DC533' : undefined,
                          color: active ? '#fff' : undefined,
                        }}
                      >
                        {d.label}
                      </Button>
                    );
                  })}
                </Space>
              )}
            </div>
          )}

          {sendMode === 'daily' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sendFixedTime ? 10 : 0 }}>
                <Text strong>Fixed time each day</Text>
                <Switch checked={sendFixedTime} onChange={setSendFixedTime} />
              </div>
              {sendFixedTime ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Select showSearch style={{ width: '100%' }} value={sendDailyTz} onChange={setSendDailyTz} optionFilterProp="label" options={TIMEZONES} />
                  {sendDayMode === 'specific' ? (
                    sendDaysSelected.length ? (
                      [...sendDaysSelected]
                        .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
                        .map(d => (
                          <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text style={{ width: 42, fontSize: 13, fontWeight: 500 }}>{dayLabel(d)}</Text>
                            <TimePicker
                              use12Hours format="hh:mm A" style={{ flex: 1 }}
                              value={sendDayTimes[d] || null}
                              onChange={val => setSendDayTimes(pt => ({ ...pt, [d]: val }))}
                              placeholder="Time"
                            />
                          </div>
                        ))
                    ) : (
                      <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                        Select at least one day above to set its time.
                      </Text>
                    )
                  ) : (
                    <TimePicker
                      use12Hours format="hh:mm A" style={{ width: '100%' }}
                      value={sendDailyClock}
                      onChange={setSendDailyClock}
                      placeholder="Time"
                    />
                  )}
                </div>
              ) : (
                <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>
                  Off — each batch fires ~24h after the previous one, whenever that happened to be.
                </Text>
              )}
            </div>
          )}

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>When</Text>
            <Radio.Group value={sendTiming} onChange={e => setSendTiming(e.target.value)} style={{ width: '100%' }}>
              <Radio.Button value="now" style={{ width: '50%', textAlign: 'center' }}>Start now</Radio.Button>
              <Radio.Button value="schedule" style={{ width: '50%', textAlign: 'center' }}>Schedule start</Radio.Button>
            </Radio.Group>
          </div>

          {sendTiming === 'schedule' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <Text style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Timezone</Text>
                <Select showSearch size="large" style={{ width: '100%' }} value={sendStartTz} onChange={setSendStartTz} optionFilterProp="label" options={TIMEZONES} />
              </div>
              <div>
                <Text style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>{sendMode === 'daily' ? 'First batch date & time' : 'Date & time'}</Text>
                <DatePicker
                  showTime={{ use12Hours: true, format: 'hh:mm A' }}
                  format="YYYY-MM-DD hh:mm A"
                  style={{ width: '100%', borderRadius: 10 }}
                  size="large"
                  disabledDate={d => d && d < dayjs().startOf('day')}
                  onChange={val => setSendStartDate(val ? val.toDate() : null)}
                />
              </div>
            </div>
          )}

          <Text style={{ color: themeToken.colorTextSecondary, fontSize: 13 }}>
            {sendMode === 'daily'
              ? <>
                  Sends up to <strong>{sendDaily || 0}</strong>/day{sendTiming === 'schedule' ? ' starting at the chosen time' : ''}
                  {sendDayMode === 'specific' && sendDaysSelected.length > 0
                    ? <> on <strong>{[...sendDaysSelected].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b)).map(dayLabel).join(', ')}</strong> only</>
                    : ''}
                  {sendFixedTime && sendDayMode === 'specific' && Object.keys(sendDayTimes).length > 0
                    ? <> at <strong>
                        {[...sendDaysSelected]
                          .filter(d => sendDayTimes[d])
                          .sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))
                          .map(d => `${dayLabel(d)} ${dayjs(sendDayTimes[d]).format('hh:mm A')}`)
                          .join(' · ')} {sendDailyTz}
                      </strong></>
                    : (sendFixedTime && sendDayMode !== 'specific' && sendDailyClock ? <> at <strong>{dayjs(sendDailyClock).format('hh:mm A')} {sendDailyTz}</strong> each day</> : '')}
                  , then continues until the whole list is done. Already-sent subscribers are never emailed twice. You can change this schedule anytime while it's running.
                </>
              : <>The entire list will be sent {sendTiming === 'schedule' ? 'at the scheduled time' : 'immediately'} (respecting your SES rate limit and suppression list).</>}
          </Text>
        </div>
      </Modal>

      {/* Preview & Send Test Modal */}
      {(() => {
        const tc = campaigns.find(c => c.id === testModal);
        const previewHtml = (tc?.html_content || '')
          .replace(/\{\{first_name\}\}/gi, 'there')
          .replace(/\{\{full_name\}\}/gi, 'Friend')
          .replace(/\{\{email\}\}/gi, testEmail || 'you@example.com')
          .replace(/\{\{(city|state|country)\}\}/gi, '')
          .replace(/\{\{[^}]+\}\}/g, '');
        return (
          <Modal
            title={<Space><ExperimentOutlined style={{ color: '#6366f1' }} /><span>Preview &amp; Send Test</span></Space>}
            open={!!testModal}
            onCancel={() => { setTestModal(null); setTestEmail(''); }}
            onOk={handleTestSend}
            okText="Send Test"
            okButtonProps={{ loading: testSending, disabled: !testEmail, style: { borderRadius: 8 } }}
            cancelButtonProps={{ style: { borderRadius: 8 } }}
            width={720}
          >
            <div style={{ marginTop: 16 }}>
              {tc && (
                <div style={{ marginBottom: 12 }}>
                  <Text style={{ fontSize: 12, color: themeToken.colorTextTertiary }}>Subject</Text>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{tc.subject}</div>
                </div>
              )}
              <div style={{ border: `1px solid ${themeToken.colorBorderSecondary}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16, background: '#fff' }}>
                {previewHtml
                  ? <iframe title="Email preview" srcDoc={previewHtml} style={{ width: '100%', height: 360, border: 'none', display: 'block' }} sandbox="" />
                  : <div style={{ padding: 40, textAlign: 'center', color: themeToken.colorTextTertiary }}>No content to preview.</div>}
              </div>
              <Text style={{ color: themeToken.colorTextSecondary, display: 'block', marginBottom: 8 }}>
                Send this test to any email address:
              </Text>
              <Input
                type="email"
                placeholder="test@example.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                onPressEnter={handleTestSend}
                size="large"
                style={{ borderRadius: 10 }}
              />
            </div>
          </Modal>
        );
      })()}

      {/* Schedule / Reschedule Modal */}
      <Modal
        title={<Space><ClockCircleOutlined style={{ color: '#6366f1' }} /><span>{scheduleIsReschedule ? 'Reschedule Campaign' : 'Schedule Campaign'}</span></Space>}
        open={!!scheduleModal}
        onCancel={() => { setScheduleModal(null); setScheduleDate(null); }}
        onOk={handleSchedule}
        okText={scheduleIsReschedule ? 'Reschedule' : 'Schedule'}
        okButtonProps={{ loading: scheduling, style: { borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        destroyOnHidden
        width={440}
      >
        <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Timezone</Text>
            <Select
              showSearch
              style={{ width: '100%' }}
              size="large"
              value={scheduleTz}
              onChange={setScheduleTz}
              optionFilterProp="label"
              options={TIMEZONES}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Date and time</Text>
            <DatePicker
              showTime={{ use12Hours: true, format: 'hh:mm A' }}
              format="YYYY-MM-DD hh:mm A"
              style={{ width: '100%', borderRadius: 10 }}
              size="large"
              disabledDate={d => d && d < dayjs().startOf('day')}
              onChange={val => setScheduleDate(val ? val.toDate() : null)}
            />
            <Text style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, display: 'block' }}>
              Past times on today's date will be rejected.
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Campaigns;
